import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import {
  Grid, Alert, Avatar, IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { getPublicUserInfo } from '~common/api/user';
import { PageLoader } from '~common/components';
import PageTitle from '../../components/pageTitle';
import useStyles from './styles';

const InfoItem = ({ title, value, copy = false }) => {
  const { classes } = useStyles();
  if (!value) return null;
  return (
    <Grid container className={classes.item}>
      <Grid item xs={12} sm={4}>
        <Typography variant="body1" className={classes.itemTitle}>{title}</Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <div className={classes.valueContainer}>
          <Typography variant="body1" className={classes.itemValue}>{value}</Typography>
          {copy && (
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(value)}
              aria-label="copy email"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

InfoItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
  copy: PropTypes.bool,
};

const PublicProfile = () => {
  const { classes } = useStyles();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, err] = await getPublicUserInfo(id);
      if (err || !res) {
        setError('Unable to load user information.');
      } else {
        setData(res);
      }
    } catch (e) {
      setError('An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <PageTitle title={`${data.fullName} Profile`} />
      </div>
      <Grid container className={classes.grid}>
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        {data && (
          <Grid item xs={12} className={classes.infoContainer}>
            {/* If image exists (Tutor), show avatar */}
            {data.image && (
              <div className={classes.avatarContainer}>
                <Avatar
                  src={data.image}
                  alt={data.fullName}
                  sx={{ width: 120, height: 120 }}
                />
              </div>
            )}

            <div className={classes.titleContainer}>
              <Typography variant="body1" className={classes.title}>
                {data.fullName} Details
              </Typography>
            </div>

            <div className={classes.itemList}>
              <InfoItem title="Name" value={data.fullName} />
              <InfoItem title="Email" value={data.email} copy />

              {/* If parent info exists (Learner) */}
              <InfoItem title="Parent Name" value={data.parentName} />
              <InfoItem title="Parent Email" value={data.parentEmail} copy />
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default PublicProfile;
